// @ts-strict-ignore
import { LoggerService } from 'rainbow-web-sdk/services/core/logger.service';
import { ErrorHelperInfo, ErrorHelperService } from 'rainbow-web-sdk/services/utilities/errorHelper.service';
import { AuthService } from 'rainbow-web-sdk/services/core/auth.service';
import { DirectoryContact } from 'rainbow-web-sdk/models/directoryContact.model';
import { CompanyScopeType, DirectoryInternalSearchResults, DirectorySearchOptions } from '../core/directorySearch.service';
import { EventService } from 'rainbow-web-sdk/services/core/event.service';
import { ProfileService } from 'rainbow-web-sdk/services/core/profile.service';
import { ContactService } from 'rainbow-web-sdk/services/core/contact.service';
import { Contact } from 'rainbow-web-sdk/models/contact.model';
import { CountryService } from 'rainbow-web-sdk/services/core/country.service';
import { Company } from 'rainbow-web-sdk/models/company.model';
import { UserInfoService } from 'rainbow-web-sdk/services/core/userInfo.service';
import { Service } from 'rainbow-web-sdk/services/service';
import anonymizer from '../../utils/anonymizer';
import { MD5 } from '..';

export const COMPANY_DIRECTORY_SVC = 'CompanyDirectoryService';

/**
 * @enum {string}
 * @public
 * @readonly
 * @description
 *      Collection of events fired by {@link CompanyDirectoryService}
 */
export enum CompanyDirectoryEvent {

    /**
     * @public
     * @event CompanyDirectoryEvent#ADMIN_DIRECTORY_CHANGE
     * @description
     *      This event is fired when directory changes
     *      The event contains a {@link DirectoryContact} object
     * @param {DirectoryContact} data containeing id: ID of invitation, action: action of invitation, status: status of invitation
     */
    ADMIN_DIRECTORY_CHANGE = "ADMIN_DIRECTORY_CHANGE",

    /**
     * @public
     * @event CompanyDirectoryEvent#ON_COMPANY_BANNER_UPDATED
     * @description
     *      This event is fired when the banner of a company changes
     *      The event contains the identifier of the company
     * @param {string} id Identifier of company updated
     */
    ON_COMPANY_BANNER_UPDATED = "ON_COMPANY_BANNER_UPDATED"
}

export class CompanyDirectoryService extends Service {

    // ====================================================
    // Services used
    // ====================================================
    private logger: LoggerService = LoggerService.getInstance();
    private authService: AuthService = AuthService.getInstance();
    private eventService: EventService = EventService.getInstance();
    private profileService: ProfileService = ProfileService.getInstance();
    private contactService: ContactService = ContactService.getInstance();
    private countryService: CountryService = CountryService.getInstance();
    private userInfoService: UserInfoService = UserInfoService.getInstance();
    private errorHelperService: ErrorHelperService = ErrorHelperService.getInstance();

    private companies: Company[] = [];

    public static getInstance() {
        return Service.create(COMPANY_DIRECTORY_SVC, CompanyDirectoryService);
    }

    public static build() {
        return new CompanyDirectoryService();
    }

    protected constructor() {
        super();
    }

    // ====================================================
    //                      PUBLIC API
    // ====================================================

    /**
     *  Get a list of directory entries data (API request to {@link https://api.openrainbow.org/directory/#api-directory-GetDirectoryList Rainbow Company Directory portal - directory - Get a list of directory entries data})
     * @public
     * @description
     *      Get a list of contacts from company's directory
     * @param string - companyId Allows to filter the list of directory entries on the companyIds provided in this option
     * @param page - Allow to specify the page number to retrieve
     * @param pageSize - Allow to specify the number of phone book entries to retrieve
     * @param searchPattern - Allows to filter the list of directory entries by the words provided in this option
     * @param searchBy - Allows to filter the list of directory entries on the number provided in this option.
     * @param format - Allows to retrieve more or less entry details in response. Possible values are:
     *      - small: id, firstName, lastName
     *      - medium: id, companyId, firstName, lastName, workPhoneNumbers
     *      - full: all fields.
     * @returns {Promise<{ contacts: DirectoryContact[]; limit: number; offset: number; total: number }>} object contaning result of search
     * @memberof CompanyDirectoryService
     */
    public async getDirectoryContacts(companyId: string, page?: number, pageSize: number = 1000, searchPattern?: string, searchBy?: 'phoneNumbers' | 'tags' | 'name', format: string = 'full'): Promise<{ contacts: DirectoryContact[]; limit: number; offset: number; total: number; }> {
        try {

            // Prepare request url
            const portalURL = config.restServerUrl + "/api/rainbow/directory/v1.0/";
            let url: string = `${portalURL}entries?view=company&type=all&format=${format}&companyId=${companyId}&limit=${pageSize}`;
            if (page) { url += "&offset=" + (pageSize * (page - 1)); }

            if (searchPattern) {
                switch (searchBy) {
                    case "phoneNumbers":
                    case "tags":
                        url += "&" + searchBy + "=" + encodeURIComponent(searchPattern);
                        break;
                    case "name":
                    default:
                        url += "&name=" + encodeURIComponent(searchPattern);
                        break;
                }
            }

            const headers = this.authService.getRequestHeader();
            const response: Response = await fetch(url, { method: 'GET', headers });
            if (!response.ok) { throw (response); }
            const responseData: any = (await response.json());
            this.logger.info("[CompanyDirectoryService] getDirectoryContacts success (found " + responseData.total + " contact(s))");
            const contactsData = responseData.data;
            const contacts: DirectoryContact[] = [];

            contactsData.forEach((contactData) => {
                const contact = DirectoryContact.createFromData(contactData);
                contacts.push(contact);
            });

            return { contacts: contacts, limit: responseData.limit, offset: responseData.offset, total: responseData.total };

        } catch (error) {
            const errorInfo = await this.errorHelperService.getErrorInfo(error, 'getDirectoryContacts');
            this.logger.error(`[CompanyDirectoryService] ${errorInfo.message}`);
            throw errorInfo.error;
        }
    }

    /**
     * This API allows administrators to create an entry in the directory of a company they administrate. (API request to {@link https://api.openrainbow.org/directory/#api-directory-PostDirectory Rainbow Company Directory portal - directory - Create a directory entry})
     * @public
     * @description
     *      Creates a company directory contact
     * @param {DirectoryContact} entry a {@link DirectoryContact} object to create on server
     * @returns {Promise<DirectoryContact>} The created company directory contact
     * @memberof CompanyDirectoryService
     * @fires CompanyDirectoryEvent#ADMIN_DIRECTORY_CHANGE
     */
    public async createDirectoryContact(entry: DirectoryContact): Promise<DirectoryContact> {
        try {
            if (!entry.companyId) {
                throw (new Error("Missing mandatory parameter 'companyId"));
            }
            const portalURL = config.restServerUrl + "/api/rainbow/directory/v1.0/";

            const url: string = `${portalURL}entries`;
            const headers = this.authService.getPostHeader();
            const response: Response = await fetch(url, { method: 'POST', headers, body: JSON.stringify(entry) });
            if (!response.ok) { throw (response); }
            const responseData: any = (await response.json());
            const contact = DirectoryContact.createFromData(responseData.data);
            this.logger.info("[CompanyDirectoryService] createDirectoryContact success");

            this.eventService.publish(CompanyDirectoryEvent.ADMIN_DIRECTORY_CHANGE, contact);

            return contact;
        } catch (error) {
            const errorInfo = await this.errorHelperService.getErrorInfo(error, 'createDirectoryContact');
            this.logger.error(`[CompanyDirectoryService] ${errorInfo.message}`);
            throw errorInfo.error;
        }
    }

    /**
     * This API allows users to get an entry of the directory of a company they administrate. (API request to {@link https://api.openrainbow.org/directory/#api-directory-GetDirectory Rainbow Company Directory portal - directory - Get a directory entry data})
     * @public
     * @description
     *      Retrieve a company directory contact from its identifier
     * @param {string} id Identifier of contact to retrieve
     * @param {string} [format] Allows to retrieve more or less entry details in response. Possible values are:
     *      - small: id, firstName, lastName
     *      - medium: id, companyId, firstName, lastName, workPhoneNumbers
     *      - full: all fields.
     * @returns {Promise<DirectoryContact>} the {@link DirectoryContact} retrieved
     * @memberof CompanyDirectoryService
     */
    public async getDirectoryContact(id: string, format: string = 'full'): Promise<DirectoryContact> {
        try {
            const portalURL = config.restServerUrl + "/api/rainbow/directory/v1.0/";
            const url: string = `${portalURL}entries/${id}?format=${format}`;
            const headers = this.authService.getRequestHeader();
            const response: Response = await fetch(url, { method: 'GET', headers });
            if (!response.ok) { throw (response); }
            const responseData: any = (await response.json());
            this.logger.info("[CompanyDirectoryService] getDirectoryContact success");
            const contact = DirectoryContact.createFromData(responseData.data);
            return contact;
        } catch (error) {
            const errorInfo = await this.errorHelperService.getErrorInfo(error, 'getDirectoryContact');
            this.logger.error(`[CompanyDirectoryService] ${errorInfo.message}`);
            throw errorInfo.error;
        }
    }

    /**
     * This API allows administrators to update an entry in the directory of a company they administrate. (API request to {@link https://api.openrainbow.org/directory/#api-directory-PutDirectory Rainbow Company Directory portal - directory - Update a directory entry})
     * @public
     * @description
     *  Updates a company directory contact
     * @param {({ id: string; tags: string[] } | DirectoryContact)} entry of an existing company directory contact or a data object containing a company contact's ID and tags
     * @returns {Promise<DirectoryContact>} the {@link DirectoryContact} updated
     * @memberof CompanyDirectoryService
     */
    public async updateDirectoryContact(entry: { id: string; tags: string[]; } | DirectoryContact): Promise<DirectoryContact> {
        try {
            const portalURL = config.restServerUrl + "/api/rainbow/directory/v1.0/";
            const url: string = `${portalURL}entries/${entry.id}`;
            const headers = this.authService.getPostHeader();
            const response: Response = await fetch(url, { method: 'PUT', headers, body: JSON.stringify(entry) });
            if (!response.ok) { throw (response); }
            const responseData: any = (await response.json());
            const contact = DirectoryContact.createFromData(responseData.data);
            this.logger.info("[CompanyDirectoryService] createDirectoryContact success");

            this.eventService.publish(CompanyDirectoryEvent.ADMIN_DIRECTORY_CHANGE, contact);

            return contact;

        } catch (error) {
            const errorInfo = await this.errorHelperService.getErrorInfo(error, 'updateDirectoryContact');
            this.logger.error(`[CompanyDirectoryService] ${errorInfo.message}`);
            throw errorInfo.error;
        }
    }

    /**
     * This API allows administrators to delete an entry in the directory of a company they administrate. (API request to {@link https://api.openrainbow.org/directory/#api-directory-DeleteDirectory Rainbow Company Directory portal - directory - Delete a directory entry})
     * @public
     * @description
     *  Deletes a company directory contact from comany's directory
     * @param {string} id Identifier of contact to delete
     * @returns {Promise<void>} resolves when delete is done
     * @memberof CompanyDirectoryService
     */
    public async deleteDirectoryContact(id: string): Promise<void> {
        try {
            const portalURL = config.restServerUrl + "/api/rainbow/directory/v1.0/";
            const url: string = `${portalURL}entries/${id}`;
            const headers = this.authService.getPostHeader();
            const response: Response = await fetch(url, { method: 'DELETE', headers });
            if (!response.ok) { throw (response); }

            this.logger.info(`[CompanyDirectoryService] deleteDirectoryContact of ${id} success`);

            this.eventService.publish(CompanyDirectoryEvent.ADMIN_DIRECTORY_CHANGE);

            return;

        } catch (error) {
            const errorInfo = await this.errorHelperService.getErrorInfo(error, 'deleteDirectoryContact');
            this.logger.error(`[CompanyDirectoryService] ${errorInfo.message}`);
            throw errorInfo.error;
        }
    }

    /**
     * This API allows users to search business directory contacts by specifying a pattern
     * (API request to {@link https://api.openrainbow.org/directory/#api-directory-GetDirectoryList Rainbow Company Directory portal - directory - Get a list of directory entries data})
     * @public
     * @description
     * Search contacts in the business directory of one or more companies by using a specified pattern to filter items.
     * Concerning 'extendedMode' in 'options' parameter:
     * If requested and the user has the feature SEARCH_BY_TAGS in its profile, a multi-criteria search will then be performed.
     * The multi-criteria search applies on fields firstName, lastName, jobTitle, companyName, department and tags.
     * If this option is not specified or the user does not have the feature SEARCH_BY_TAGS in his profile,
     * the search is based on names only (first name, last name and company name).
     * @param {string} searchPattern Pattern to search
     * @param {DirectorySearchOptions} [options] Search options {@link DirectorySearchOptions}
     * @returns {Promise<DirectoryInternalSearchResults[]>} Resolves with an array of {@link DirectoryInternalSearchResults} results and
     * the total number of directory items matching the criteria.
     * @memberof CompanyDirectoryService
     */
    public async searchBusinessContacts(searchPattern: string, options?: DirectorySearchOptions): Promise<DirectoryInternalSearchResults> {
        try {
            this.logger.info(`[searchBusinessContacts] search business contacts with pattern ${anonymizer(searchPattern)}`);

            const portalURL = config.restServerUrl + "/api/rainbow/directory/v1.0";

            // Define useful search options
            const searchOptions: any = {};
            searchOptions.type = "all";
            searchOptions.view = "company";
            searchOptions.format = "full";

            if (options?.limit) { searchOptions.limit = options.limit; }

            // Simple OR Multi criteria search ?
            const searchByTagAllowed = this.profileService.isFeatureEnabled(this.profileService.FeaturesEnum.SEARCH_BY_TAGS);
            let searchType = "";

            if (options?.extendedMode) {
                if (searchByTagAllowed) {
                    searchType = options.extendedMode ? "search" : "name";
                } else {
                    if (options.extendedMode) {
                        this.logger.warn(`[CompanyDirectoryService] searchBusinessContacts -- 'extendedMode' option not allowed and ignored `);
                    }
                    searchType = "name";
                }
            } else {
                searchType = "name";
            }
            if (searchType === "name") {
                searchOptions.name = searchPattern;
            } else {
                searchOptions.search = searchPattern;
            }

            if (options?.company?.companyScope) {
                switch (options?.company?.companyScope) {
                    case CompanyScopeType.MY_COMPANY:
                        searchOptions.companyId = this.contactService.userContact.company.id;
                        break;

                    default:
                        this.logger.warn(`[CompanyDirectoryService] searchBusinessContacts -- 'companyScope' option not managed and ignored `);
                        break;
                }
            }

            // Build Request for Server
            const url = new URL(`${portalURL}/entries`);
            const urlParams = new URLSearchParams(searchOptions).toString();
            const headers = this.authService.getRequestHeader();
            url.search = urlParams;
            const response = await fetch(url.toString(), { method: 'GET', headers: headers });

            if (!response.ok) { throw (response); }
            const responseData: any = (await response.json());
            const contacts = responseData.data;
            this.logger.info(`[CompanyDirectoryService] search success -- Found ${responseData.total} contact(s)`);

            const companyContacts = contacts.map((contactData) => {
                return this.createAndUpdateContact(contactData);
            });
            this.logger.info(`[CompanyDirectoryService] searchBusinessContacts -- Return ${companyContacts.length} contact(s)`);

            return { contacts: companyContacts, total: responseData.total };
        } catch (error) {
            const errorInfo: ErrorHelperInfo = await this.errorHelperService.getErrorInfo(error, 'searchBusinessContacs');
            this.logger.error(`[CompanyDirectoryService] ${errorInfo.message}`);
            throw errorInfo.error;
        }
    }

    private createAndUpdateContact(contactData: any): Contact {
        const contact: Contact = this.contactService.createDirectoryContact(contactData.id, false);

        // Set specific data which does not come from the contact itself but useful to manage and identify
        // the company contact ( from Rainbow Business Contact Directory) in contact Model
        // These flags must be set BEFORE the update of the created contact names (via 'updateName') because some are deduced
        // from the firstname/lastname OR the company name and the contact type.
        contact.companyDirectoryFlag = true;
        contact.personalDirectoryFlag = false;

        // Phone numbers
        contact.mobilePhones = contactData.mobilePhoneNumbers;
        contact.businessPhones = contactData.workPhoneNumbers;
        contact.aliasesPhones = contactData.aliases; //RQRAINB-8992
        contact.otherPhoneNumbers = contactData.otherPhoneNumbers;

        contact.email = contactData.eMail;
        contact.businessCompanyId = contactData.companyId;
        contact.companyName = contactData.companyName;
        contact.department = contactData.department;
        contact.jobTitle = contactData.jobTitle;
        //contact.company = contactService.userContact.company;

        contact.updateName(contactData.firstName, contactData.lastName);
        contact.setAvatarFromDefault();

        contact.note1 = contactData.custom1;
        contact.note2 = contactData.custom2;
        contact.tags = contactData.tags ? contactData.tags : undefined;

        // Business address
        contact.city = contactData.city;
        contact.postalCode = contactData.postalCode;
        contact.street = contactData.street;
        contact.state = this.countryService.getStateName(contactData.country, contactData.state);
        contact.country = this.countryService.getCountryName(contactData.country);

        return contact;
    }

    /**
     *  Get a list of directory entries data (API request to {@link https://api.openrainbow.org/directory/#api-directory-GetDirectoryList Rainbow Company Directory portal - directory - Get a list of directory entries data})
     * @public
     * @description
     *      Search a company by name
     * @param {string} searchPattern text to search
     * @param {string} [companyId] If specified, search only in the given company
     * @returns {Promise<Company[]>} Resolves with an array of {@link Company} results
     * @memberof CompanyDirectoryService
     */
    public async searchCompanies(searchPattern: string, companyId?: string): Promise<Company[]> {
        try {
            const portalURL = config.restServerUrl + "/api/rainbow/directory/v1.0/";
            const url: string = `${portalURL}entries?view=company&format=full&companyName=${encodeURIComponent(searchPattern)}${companyId ? `&companyId=${companyId}` : ''}`;
            const headers = this.authService.getRequestHeader();
            const response: Response = await fetch(url, { method: 'GET', headers });
            if (!response.ok) { throw (response); }
            const responseData: any = (await response.json());
            const directoryCompaniesData = responseData.data;

            const companies = directoryCompaniesData.map((directoryCompanyData) => {
                const company = Company.create(directoryCompanyData.id, directoryCompanyData.companyName);
                company.country = directoryCompanyData.country;
                // Load asynchronously company logo (not banner!)
                this.getCompanyLogo(company);
                this.getCompanyBanner(company);
                this.companies[company.id] = company;
                return company;
            });
            this.logger.info("[CompanyDirectoryService] searchCompanies success");
            return companies;

        } catch (error) {
            const errorInfo = await this.errorHelperService.getErrorInfo(error, 'searchCompanies');
            this.logger.error(`[CompanyDirectoryService] ${errorInfo.message}`);
            return [];
        }
    }

    /**
     *
     * @public
     * @description
     *      Get the company's logo
     * @param {Company} company for which we want the logo
     * @returns {Promise<Company>} Resolves with the company with the logo
     * @memberof CompanyDirectoryService
     */
    public async getCompanyLogo(company: Company): Promise<Company> {
        try {
            const companyColor = this.userInfoService.computeUserColor(company.name);
            const companyInitials = company.name.substr(0, 1);
            company.logo = this.userInfoService.getAvatarImage(company.id, companyInitials, companyColor.color, 130, company.lastAvatarUpdateDate);
            this.eventService.publish("ON_COMPANY_LOGO_UPDATED", company.id);
            this.logger.info("[CompanyDirectoryService] - getCompanyLogo - " + company.getNameForLogs() + " - success");
            company.sendChangeEvent('logo');
            return company;
        }
        catch (error) {
            throw (new Error(`getCompanyLogo -- FAILURE -- ${error.message}`));
        }
    }

    /**
     *
     * @public
     * @description
     *      Get the company's banner
     * @param {Company} company for which we want the banner
     * @returns {Promise<Company>} Resolves with the company with the banner
     * @memberof CompanyDirectoryService
     */
    public async getCompanyBanner(company: Company): Promise<Company> {
        return new Promise<Company>((resolve) => {
            const image = new Image();

            image.onload = () => {
                company.banner = image;
                this.eventService.publish(CompanyDirectoryEvent.ON_COMPANY_BANNER_UPDATED, company.id);
                this.logger.info("[CompanyDirectoryService] - getCompanyBanner - " + company.getNameForLogs() + " - success");
                resolve(company);
            };

            image.onerror = () => {
                this.logger.error("[CompanyDirectoryService] Load banner for " + company.getNameForLogs() + " failure");
                resolve(company);
            };

            // Load the image

            let imgSrc = config.sdk ? null : "/cacheV2/images/company-banner-01.svg";
            if (company.lastBannerUpdateDate) {
                const serverURL = config.webservices.protocol + "://" + config.webservices.currentServer;
                imgSrc = serverURL + "/api/banner/" + company.id + "?size=831";
                imgSrc += "&update=" + MD5(company.lastBannerUpdateDate);
            }
            if (imgSrc) { image.src = imgSrc; }
            else resolve(company);
        });
    }

    /**
     *
     * @public
     * @description
     *      Get a {@link Company} by its identifier
     * @param {string} companyId ID of company to retrieve
     * @returns {Company} Company found. undefined if none found
     * @memberof CompanyDirectoryService
     */
    public getCompanyById(companyId: string): Company {
        if (!companyId) { throw (new Error('missing companyId')); }

        // Try to get company from cache
        const company = this.companies[companyId];
        this.logger.info("[CompanyDirectoryService] getCompanyById (cache) " + companyId + " - success");
        return company;
    }

    // ====================================================
    //                      EXPORT CSV
    // ====================================================

    /**
     *
     * @public
     * @description
     *      Method retrieve all  company's directory contact
     * @param {string} companyId Identifier of company to retrieve
     * @returns {Promise<{ contacts: DirectoryContact[]; limit: number }>} Resolves with object contaning result of search
     * @memberof CompanyDirectoryService
     */
    public async getAllDirectoryContacts(companyId: string): Promise<{ contacts: DirectoryContact[]; limit: number; }> {
        try {
            const limit = 1000; // maximum of entries that can be requested to the server

            // Get first page of entries and the total number of entries to retrieve
            const firstResponse = await this.getDirectoryContacts(companyId, 1, limit);
            const result = firstResponse;
            if (firstResponse.total > limit) { // total is higher than the liit we set so other requests are necessary
                const totalPages = Math.ceil(firstResponse.total / limit); // Compute the number of pages to retrieve
                // List of page numbers to get (remove first page that was already gotten)
                let pages = [...Array(totalPages - 1)];
                pages = pages.map((__unused, index) => { return index + 2; }); // fill array with page numbers to request

                // Serialize promises by chunks (avoids more requests than the server can handle)
                const chunks = [];
                while (pages.length > 0) { chunks.push(pages.splice(0, 5)); } // chunk size must be less than 10 to avoid internal system error

                await chunks.reduce((promiseChain, requests) => {
                    // Parallelize chunks
                    return promiseChain.then(() => {
                        const promisesArray = requests.map((page) => {
                            return this.getDirectoryContacts(companyId, page, limit).then((data) => {
                                result.contacts = result.contacts.concat(data.contacts);
                                result.limit += data.limit;
                            });
                        });
                        // return chain
                        return Promise.all(promisesArray);
                    });
                }, Promise.resolve());

            }
            return result;

        } catch (error) {
            this.logger.error(`[CompanyDirectoryService] getAllDirectoryContacts - ${error.message}`);
            throw (error);
        }
    }

    public async buildDirectoryCsvBlob(companyId: string) {
        try {
            this.logger.info("[CompanyDirectoryService] === buildDirectoryCsvBlob ===");
            const result = await this.getAllDirectoryContacts(companyId);

            // Find number of csv's "workPhoneNumber" columns "mobilePhoneNumber" and "otherPhoneNumber" columns (at least workPhoneNumber0, mobilePhoneNumber0 and otherPhoneNumber0)
            const maxWorkPhoneNumbers = Math.max(1, result.contacts.reduce((max, contact) => {
                return Math.max(max, contact.workPhoneNumbers ? contact.workPhoneNumbers.length : 0);
            }, 0));
            const maxMobilePhoneNumbers = Math.max(1, result.contacts.reduce((max, contact) => {
                return Math.max(max, contact.mobilePhoneNumbers ? contact.mobilePhoneNumbers.length : 0);
            }, 0));
            const maxOtherPhoneNumbers = Math.max(1, result.contacts.reduce((max, contact) => {
                return Math.max(max, contact.otherPhoneNumbers ? contact.otherPhoneNumbers.length : 0);
            }, 0));
            const csvWorkPhonesColumns = [...new Array(maxWorkPhoneNumbers)].map((__val, i) => { return "workPhoneNumber" + i; });
            const csvWorkPhonesExtraSeparators = [...new Array(maxWorkPhoneNumbers)].map(() => { return ";"; });
            const csvMobilePhonesColumns = [...new Array(maxMobilePhoneNumbers)].map((__val, i) => { return "mobilePhoneNumber" + i; });
            const csvMobilePhonesExtraSeparators = [...new Array(maxMobilePhoneNumbers)].map(() => { return ";"; });
            const csvOtherPhonesColumns = [...new Array(maxOtherPhoneNumbers)].map((__val, i) => { return "otherPhoneNumber" + i; });
            const csvOtherPhonesExtraSeparators = [...new Array(maxOtherPhoneNumbers)].map(() => { return ";"; });

            // Find number of csv's "aliasType" and "aliasCode" columns (at least aliasType0, aliasCode0)
            const maxAliases = Math.max(1, result.contacts.reduce((max, contact) => {
                return Math.max(max, contact.aliases ? contact.aliases.length : 0);
            }, 0));
            const csvAliasesColumns = [...new Array(maxAliases)].map((__val, i) => { return "aliasType" + i + ";aliasCode" + i; });
            const csvAliasesExtraSeparators = [...new Array(maxAliases)].map(() => { return ";;"; });

            // directory csv file header line
            const csvDirectoryLines: string[] = [];
            csvDirectoryLines.push("firstName;lastName;companyName;department;street;city;postalCode;state;country;" + csvWorkPhonesColumns.join(";") + ";" + csvMobilePhonesColumns.join(";") + ";" + csvOtherPhonesColumns.join(";") + ";" + csvAliasesColumns.join(";") + ";jobTitle;eMail;tag0;tag1;tag2;tag3;tag4;custom1;custom2");

            result.contacts.forEach((contact) => {
                let contactLine = "";
                contactLine += contact.firstName ? contact.firstName : "";

                contactLine += ";";
                contactLine += contact.lastName ? contact.lastName : "";

                contactLine += ";";
                contactLine += contact.companyName ? contact.companyName : "";

                contactLine += ";";
                contactLine += contact.department ? contact.department : "";

                contactLine += ";";
                contactLine += contact.street ? contact.street : "";

                contactLine += ";";
                contactLine += contact.city ? contact.city : "";

                contactLine += ";";
                contactLine += contact.postalCode ? contact.postalCode : "";

                contactLine += ";";
                contactLine += contact.state ? contact.state : "";

                contactLine += ";";
                contactLine += contact.country ? contact.country : "";

                // Add contact's phone numbers
                let phoneNumbers;
                let extraSeparators;
                // Add contact's work phone numbers
                phoneNumbers = contact.workPhoneNumbers && contact.workPhoneNumbers.length > 0 ? contact.workPhoneNumbers : [""]; // there is at least workPhone0 and mobilePhone0 (empty by default)
                contactLine += ";";
                contactLine += phoneNumbers.map((number) => { return number; }).join(";");
                extraSeparators = csvWorkPhonesExtraSeparators.slice(phoneNumbers.length);
                contactLine += extraSeparators.join("");
                // Add contact's mobile phone numbers
                phoneNumbers = contact.mobilePhoneNumbers && contact.mobilePhoneNumbers.length > 0 ? contact.mobilePhoneNumbers : [""]; // there is at least workPhone0 and mobilePhone0 (empty by default)
                contactLine += ";";
                contactLine += phoneNumbers.map((number) => { return number; }).join(";");
                extraSeparators = csvMobilePhonesExtraSeparators.slice(phoneNumbers.length);
                contactLine += extraSeparators.join("");
                // Add contact's other phone numbers
                phoneNumbers = contact.otherPhoneNumbers && contact.otherPhoneNumbers.length > 0 ? contact.otherPhoneNumbers : [""]; // there is at least workPhone0 and mobilePhone0 (empty by default)
                contactLine += ";";
                contactLine += phoneNumbers.map((number) => { return number; }).join(";");
                extraSeparators = csvOtherPhonesExtraSeparators.slice(phoneNumbers.length);
                contactLine += extraSeparators.join("");

                // Add aliases
                let aliases;
                aliases = contact.aliases && contact.aliases.length > 0 ? contact.aliases : [{ type: "", code: "" }]; // there is at least aliasType0 and aliasCode0 (empty by default)
                contactLine += ";";
                contactLine += aliases.map((alias) => { return alias.type + ";" + alias.code; }).join(";");
                extraSeparators = csvAliasesExtraSeparators.slice(aliases.length);
                contactLine += extraSeparators.join("");

                // Add other fields
                contactLine += ";";
                contactLine += contact.jobTitle ? contact.jobTitle : "";
                contactLine += ";";
                contactLine += contact.eMail ? contact.eMail : "";

                // Tags (max 5 tags)
                contactLine += ";";
                contactLine += contact.tags && contact.tags.length > 0 ? contact.tags[0] : "";
                contactLine += ";";
                contactLine += contact.tags && contact.tags.length > 1 ? contact.tags[1] : "";
                contactLine += ";";
                contactLine += contact.tags && contact.tags.length > 2 ? contact.tags[2] : "";
                contactLine += ";";
                contactLine += contact.tags && contact.tags.length > 3 ? contact.tags[3] : "";
                contactLine += ";";
                contactLine += contact.tags && contact.tags.length > 4 ? contact.tags[4] : "";

                contactLine += ";";
                contactLine += contact.custom1 ? contact.custom1 : "";
                contactLine += ";";
                contactLine += contact.custom2 ? contact.custom2 : "";

                csvDirectoryLines.push(contactLine);
            });

            // create blob
            const DEFAULT_CSV_BOM = "\uFEFF"; // a file encoded with UTF-8-sig starts with a BOM (byte order mark), which is useful for UTF-16 and UTF-32 but really unnecessary for UTF-8
            const DEFAULT_CSV_TYPE = "text/csv; charset=utf-8-sig";

            const directoryBlob = new Blob([DEFAULT_CSV_BOM + csvDirectoryLines.join("\r\n")], { type: DEFAULT_CSV_TYPE });

            return directoryBlob;

        } catch (error) {
            this.logger.error(`[CompanyDirectoryService] buildDirectoryCsvBlob - ${error.message}`);
            throw (error);
        }
    }

    /**
     * @public
     * @description
     *      Methods exports a csv file from specified company directory
     * @param {string} companyId identifier of company
     * @returns {Promise<{fileBlob: Blob; fileName: string}>} Resolves with the file blob and name
     * @memberof CompanyDirectoryService
     */
    public async exportDirectoryCsvFile(companyId: string): Promise<{ fileBlob: Blob; fileName: string; }> {
        try {
            this.logger.info("[CompanyDirectoryService] === exportDirectoryCsvFile ===");
            const mDate = moment();
            const csvFilename = "directory_" + mDate.format("YYYY-MM-DD_HH-mm") + ".csv";

            const fileBlob = await this.buildDirectoryCsvBlob(companyId);
            return { fileBlob: fileBlob, fileName: csvFilename };
        } catch (error) {
            this.logger.error(`[CompanyDirectoryService] exportDirectoryCsvFile - ${error.message}`);
            throw (error);
        }
    }

    // ==================================================================================
    //         COMMON Functions to manage both Business/Personal Directory
    // These functions are present here because the Company Directory Service is agnostic
    // (no start/stop mechanism) contrary to the Personal Directory Service.
    // This function can be used at any time before another directory service is launched
    // ==================================================================================

    /**
     * This API allows users to get an entry of the personal/business directory from its identifier
     * @private
     * @description
     *      Retrieve a personal/business directory contact from its identifier AND store it in Contact database
     * @memberof CompanyDirectoryService
     * @param {string} id Identifier of contact to retrieve
     * @param {boolean} forceServer Indicate if the contact must be retrieved from the server instead of the internal cache.
     * Beware ! It may not be present in the latter and in this case, a request to server is still necessary.
     * @returns {Promise<Contact>} Created contact or null if creation failed or Contact id not found
     */
    public async getDirectoryContactById(id: string, forceServer: boolean = false): Promise<Contact | null> {
        try {
            let directoryContact: Contact | null = null;

            if (!forceServer) {
                directoryContact = this.contactService.contacts[id];

                // If the personal/business directory contact is already present in database, return it !
                if (directoryContact) {
                    return directoryContact;
                }
            }

            const portalURL = config.restServerUrl + "/api/rainbow/directory/v1.0/";
            const url: string = `${portalURL}entries/${id}?format=full`;
            const headers = this.authService.getRequestHeader();
            const response: Response = await fetch(url, { method: 'GET', headers });
            if (!response.ok) { throw (response); }
            const responseData: any = (await response.json());
            this.logger.info("[CompanyDirectoryService] getDirectoryContactById success");

            directoryContact = this.createAndUpdateDirectoryContact(responseData.data);

            return directoryContact;
        } catch (error) {
            const errorInfo = await this.errorHelperService.getErrorInfo(error, 'getDirectoryContactById');
            this.logger.error(`[CompanyDirectoryService] ${errorInfo.message}`);
            // When getting a directory contact from its identifier, the server may return a 403 error if the contact refers to
            // a personal directory and the user does not have (or no more has) the PERSONAL_DIRECTORY feature flag in the profile.
            // This can happen, for example, in the case of a service downgrade (enterprise -> essential license).
            // In this case the error must be thrown to caller instead of simply return a null contact
            if (errorInfo.error.status === 403) {
                throw errorInfo.error;
            }
            return null;
        }
    }

    private createAndUpdateDirectoryContact(contactData: any): Contact {
        const contact: Contact = this.contactService.createDirectoryContact(contactData.id, false);

        // These flags must be set BEFORE the update of the created contact names (via 'updateName') because some are deduced
        // from the firstname/lastname OR the company name and the contact type.
        if (contactData.directoryType === 'personal') {
            contact.companyDirectoryFlag = false;
            contact.personalDirectoryFlag = true;
        } else { // Must be 'company' directoryType
            contact.companyDirectoryFlag = true;
            contact.personalDirectoryFlag = false;
        }

        // Phone numbers
        contact.mobilePhones = contactData.mobilePhoneNumbers;
        contact.businessPhones = contactData.workPhoneNumbers;
        contact.aliasesPhones = contactData.aliases; //RQRAINB-8992
        contact.otherPhoneNumbers = contactData.otherPhoneNumbers;

        contact.email = contactData.eMail;
        if (contactData.directoryType === 'company') {
            contact.businessCompanyId = contactData.companyId;
        }
        contact.companyName = contactData.companyName;
        contact.department = contactData.department;
        contact.jobTitle = contactData.jobTitle;

        // This has to be done after the update of the created contact names (firstname/lastname/company)
        contact.updateName(contactData.firstName, contactData.lastName);
        contact.setAvatarFromDefault();

        contact.note1 = contactData.custom1;
        contact.note2 = contactData.custom2;
        contact.tags = contactData.tags ? contactData.tags : [];

        // Contact ddress
        contact.city = contactData.city;
        contact.postalCode = contactData.postalCode;
        contact.street = contactData.street;

        if (contactData.directoryType === 'personal') {
            contact.country = contactData.country;
            contact.state = contactData.state;
        } else { // Must be 'company' directoryType
            contact.state = this.countryService.getStateName(contactData.country, contactData.state);
            contact.country = this.countryService.getCountryName(contactData.country);
        }

        return contact;
    }

    public async getAvailableShortCodes(companyId: string): Promise<{ type: string; code: string; }[]> {
        try {
            const portalURL = config.restServerUrl + "/api/rainbow/directory/v1.0/";
            const url: string = `${portalURL}companies/${companyId}/free-aliases`;
            const headers = this.authService.getRequestHeader();
            const response: Response = await fetch(url, { method: 'GET', headers });
            if (!response.ok) { throw (response); }
            const responseData: any = (await response.json());
            this.logger.info("[CompanyDirectoryService] getAvailableShortCodes success");
            return responseData.data;
        } catch (error) {
            const errorInfo = await this.errorHelperService.getErrorInfo(error, 'getAvailableShortCodes');
            this.logger.error(`[CompanyDirectoryService] ${errorInfo.message}`);
            throw errorInfo.error;
        }
    }

}
